import React, { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiContext } from '@api/api';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import AuditLogTable, { AuditLogTableHandle, AuditSharedFilter } from '@components/RingilComponents/audit/AuditLogTable.tsx';
import { ColumnConfig, FilterConfig, FilterType } from '@components/RingilDataTable/RingilDataTable.tsx';
import { AuditEntityTypeEnum, AuditEntryOutDTO, AuditFilter, Pageable, PageResponseAuditEntryOutDTO } from '@api/ringil-api.v2';

const NONE = undefined as unknown as string;

// Cross-company audit explorer (admin tools): admin endpoints, company + entity-type filters/columns.
const AdminAuditLog: React.FC = () => {
  const { tr } = useTranslationLgs();
  const { i18n } = useTranslation();
  const { queryAuditLogAdmin, auditActorsAdmin, auditActionsAdmin, auditCompaniesAdmin } = useContext(ApiContext);
  const [actorOptions, setActorOptions] = useState<{ label: string; value: string }[]>([]);
  const [actionOptions, setActionOptions] = useState<{ label: string; value: string }[]>([]);
  const [companyOptions, setCompanyOptions] = useState<{ label: string; value: string }[]>([]);

  const tableRef = useRef<AuditLogTableHandle>(null);
  const loadedScope = useRef<string>();
  const fetchedScope = useRef<string>();

  // Data-driven LoVs — scoped to what the caller may see, listing only values that occur. Actors and
  // actions follow the selected company (entity owner) + entity type; unset ⇒ across every one.
  const loadScopedOptions = (companyId: string, entityType: string) => {
    const scope = `${companyId ?? ''}|${entityType ?? ''}`;
    if (loadedScope.current === scope) return;
    loadedScope.current = scope;
    // A slower response for a scope that is no longer selected must not overwrite the current lists.
    const isCurrent = () => loadedScope.current === scope;
    auditActorsAdmin(companyId, entityType, NONE, NONE, data => isCurrent() && setActorOptions((data ?? []).map(a => ({ label: a.name, value: a.id }))));
    // Actions are BE-owned (label from activityKey); category/outcome/entity-type are FE keys.
    auditActionsAdmin(
      companyId,
      entityType,
      NONE,
      data => isCurrent() && setActionOptions((data ?? []).map(a => ({ label: tr(a.activityKey, a.auditAction), value: a.auditAction }))),
    );
  };

  useEffect(() => {
    loadScopedOptions(NONE, NONE);
    // Unscoped on purpose: companyName() resolves "restricted to" from this list.
    auditCompaniesAdmin('ENTITY_OWNER', NONE, NONE, NONE, data =>
      setCompanyOptions(
        (data ?? []).map(c => ({ label: c.name, value: c.id })).sort((a, b) => a.label.localeCompare(b.label)),
      ),
    );
  }, []);

  // FE enum→English defaults for the AuditLog.entityType.<VALUE> keys (standard FE pattern).
  const entityTypeLabel: Record<AuditEntityTypeEnum, string> = {
    [AuditEntityTypeEnum.PARTNER]: 'Partner',
    [AuditEntityTypeEnum.EXPEDITION]: 'Expedition',
    [AuditEntityTypeEnum.TIMESLOT]: 'Timeslot',
    [AuditEntityTypeEnum.SUPPLIER_NOTIFICATION]: 'Supplier notification',
    [AuditEntityTypeEnum.SHIPMENT]: 'Shipment',
    [AuditEntityTypeEnum.VISITOR_RECORD]: 'Visitor record',
    [AuditEntityTypeEnum.ATTACHMENT]: 'Attachment',
    [AuditEntityTypeEnum.CARRIER]: 'Carrier',
    [AuditEntityTypeEnum.LOCATION]: 'Location',
    [AuditEntityTypeEnum.RAMP]: 'Ramp',
  };

  const fetchPage = (pageable: Pageable, shared: AuditSharedFilter, tableFilter: any, onSuccess: (resp: PageResponseAuditEntryOutDTO) => void) => {
    const companyId = (tableFilter?.companyId || undefined) as unknown as string;
    const filter: AuditFilter = {
      ...shared,
      entityType: (tableFilter?.entityType || undefined) as AuditFilter['entityType'],
      entityId: tableFilter?.entityId || undefined,
    };
    loadScopedOptions(companyId, filter.entityType);
    const scope = `${companyId ?? ''}|${filter.entityType ?? ''}`;
    const scopeChanged = fetchedScope.current !== undefined && fetchedScope.current !== scope;
    fetchedScope.current = scope;
    // Selections made under the previous scope may not exist in the narrowed lists; clearing refetches.
    const staleSelections = scopeChanged ? ['actorId', 'action'].filter(key => tableFilter?.[key]?.length) : [];
    if (staleSelections.length && tableRef.current) {
      staleSelections.forEach(key => tableRef.current.applyFilterValue(key, []));
      return;
    }
    queryAuditLogAdmin(pageable, companyId, filter, onSuccess);
  };

  const companyName = (companyId: string) => companyOptions.find(c => c.value === companyId)?.label;

  // Link the entity to its detail page (PARTNER, RESERVATION so far), opening in a new tab (matches the
  // admin "login as user" flow). It's the cross-company admin view, so where we can resolve a user of
  // the entity owner company (persona/actor whose company matches the owner) we impersonate via runAs.
  // Robot/system rows carry no owner-company user, so the link is emitted without runAs (best effort —
  // relies on the admin's own view access). A deleted entity would 404 — not detectable from the row.
  const entityDetailLink = (row: AuditEntryOutDTO): string | undefined => {
    const runAsUser =
      row.onBehalfOfUserId && row.onBehalfOfCompanyId === row.entityOwnerCompanyId
        ? row.onBehalfOfUserId
        : row.actorId && row.actorCompanyId === row.entityOwnerCompanyId
          ? row.actorId
          : undefined;
    const q = runAsUser ? `?runAs=${runAsUser}` : '';
    switch (row.auditEntityType) {
      case AuditEntityTypeEnum.PARTNER:
        return `/${i18n.language}/settings/partners/${row.entityId}${q}`;
      case AuditEntityTypeEnum.TIMESLOT:
        return `/${i18n.language}/timeslots/${row.entityId}${q}`;
      case AuditEntityTypeEnum.SUPPLIER_NOTIFICATION:
        return `/${i18n.language}/supplier-notifications/${row.entityId}${q}`;
      case AuditEntityTypeEnum.EXPEDITION:
        return `/${i18n.language}/expedition/${row.entityId}${q}`;
      case AuditEntityTypeEnum.LOCATION:
        return `/${i18n.language}/customer/locations/${row.entityId}${q}`;
      default:
        return undefined;
    }
  };

  const extraColumns: ColumnConfig<AuditEntryOutDTO>[] = [
    {
      // Entity name in regular font, entity type as a smaller sub-label (saves a column — admin was
      // overflowing). Same sub-label pattern used by Performed by / On behalf of / Restricted to.
      id: 'entity',
      header: 'AuditLog.entity',
      headerDefault: 'Entity',
      order: 1,
      width: '10rem',
      render: row => {
        const label = row.entityApplicationId ?? row.entityName ?? row.entityId;
        const link = entityDetailLink(row);
        return (
          <div className="flex-col break-words">
            {link ? (
              <a href={link} target="_blank" rel="noreferrer" className="text-content-brand hover:underline">
                {label}
              </a>
            ) : (
              <div>{label}</div>
            )}
            <div className="text-xs text-content-secondary">{tr(`AuditLog.entityType.${row.auditEntityType}`, entityTypeLabel[row.auditEntityType])}</div>
          </div>
        );
      },
    },
  ];

  // Folded under Performed by rather than its own column. Admin sees the persona and its company; the
  // tenant surface masks this as IT OWNER. Omitted entirely when absent — an empty line reads better
  // than a dash now that it shares a cell.
  const onBehalfOfSecondary = (row: AuditEntryOutDTO) =>
    row.onBehalfOfUser || row.onBehalfOfUserId ? (
      <div className="mt-2 text-xs text-content-secondary">
        <div className="break-words">{row.onBehalfOfUser?.name ?? row.onBehalfOfUser?.email ?? row.onBehalfOfUserId}</div>
        {row.onBehalfOfCompanyName && <div className="break-words">{row.onBehalfOfCompanyName}</div>}
      </div>
    ) : null;

  // Folded under Company. NULL = visible to every party of the entity; set = only that company (BE
  // model §3.1). No restrictedToCompanyName on the DTO, so the name is resolved from the company LoV;
  // the raw id is the fallback when the company sits outside the ENTITY_OWNER set.
  const restrictedToSecondary = (row: AuditEntryOutDTO) =>
    row.restrictedToCompanyId ? (
      <div className="mt-2 text-xs text-content-secondary break-words">{companyName(row.restrictedToCompanyId) ?? row.restrictedToCompanyId}</div>
    ) : null;

  const extraFilters: FilterConfig[] = [
    {
      filterType: FilterType.Dropdown,
      label: tr('AuditLog.company', 'Company'),
      placeholder: tr('AuditLog.company', 'Company'),
      value: ['companyId'],
      options: companyOptions.map(o => ({ ...o, active: true })),
    },
    {
      // Single-select: AuditFilter.entityType is scalar (not widened to a list like the enum filters).
      filterType: FilterType.Dropdown,
      // `.name` = field label; sibling `AuditLog.entityType.<VALUE>` = the option labels.
      label: tr('AuditLog.entityType.name', 'Entity type'),
      placeholder: tr('AuditLog.entityType.name', 'Entity type'),
      value: ['entityType'],
      // FE value keys with English default (entity types are a small, stable, FE-owned enum).
      options: Object.values(AuditEntityTypeEnum).map(e => ({ label: tr(`AuditLog.entityType.${e}`, entityTypeLabel[e]), value: e, active: true })),
    },
  ];

  return (
    <AuditLogTable
      ref={tableRef}
      fetchPage={fetchPage}
      actorOptions={actorOptions}
      actionOptions={actionOptions}
      filterName={'admin_audit_log'}
      companyValue={row => row.entityOwnerCompanyName ?? row.entityOwnerCompanyId}
      performerOf={row => ({ user: row.actor, id: row.actorId })}
      showActorCompany={true}
      performedByExtra={onBehalfOfSecondary}
      companyExtra={restrictedToSecondary}
      showParamDiagnostics={true}
      extraColumns={extraColumns}
      extraFilters={extraFilters}
    />
  );
};

export default AdminAuditLog;
